import './TaxonomyEditor.scss'
import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../stores";
import {Button} from "@mui/material";
import {useLocation, useNavigate, useParams} from "react-router";
import type {DatabagRouteMatch, TaxonomyRouteMatch} from "../../routing/catRoutes";
import {IS_TE_DEVELOPMENT_MODE} from "../../components/visualization/taxonomy-editor/TaxonomyEditorOptions";
import {TaxonomyEditorComponent} from "./TaxonomyEditorComponent";
import {TaxonomyEditorApprovalComponent} from "./approval/TaxonomyEditorApprovalComponent";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";
import {TaxonomyApprovalComponent} from "../approval/taxonomy/TaxonomyApprovalComponent";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {TaxonomyEditorBreadcrumbs} from "../../components/visualization/taxonomy-editor/TaxonomyEditorBreadcrumbs.tsx";
import {TaxonomyEditorButtonBar} from "./TaxonomyEditorButtonBar.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";

type Props = {
    viewer?: boolean
}
export const TaxonomyEditorPage: React.FC<Props> = observer(({viewer}) => {
    const {rootAppStore, bagStore, taxonomyEditorStore, taxonomyManagerStore, p} = useStores();
    const navigate = useNavigate();
    const params = useParams<DatabagRouteMatch | TaxonomyRouteMatch>();
    const location = useLocation();
    if (!bagStore.bagId) throw new Error();

    // Trigger a desired taxonomy Id change here
    const urlTaxonomyId = 'taxonomyId' in params ? Number(params['taxonomyId']) : undefined;
    useEffect(() => {
        if (p.p.hardcodeTaxonomyId) {
            // If we have a hardcodeTaxonomyId set, always load the hardcoded one
            taxonomyManagerStore.setDesiredTaxonomyId(p.p.hardcodeTaxonomyId);
        } else if (urlTaxonomyId) {
            // Otherwise load the taxonomy from the URL
            taxonomyManagerStore.setDesiredTaxonomyId(urlTaxonomyId);
        } else {
            // Otherwise do not load the taxonomy store (and assume it's loaded somewhere else in the system)
        }
    }, [taxonomyManagerStore, urlTaxonomyId, p])

    useEffect(() => {
        taxonomyManagerStore.requestTaxonomyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // TODO: Disable edit mode for the taxonomy editor when navigation happens
    //  >>> OK

    const className = 'taxonomy-editor-page'
        + (viewer ? ' viewer' : '')
        + (IS_TE_DEVELOPMENT_MODE ? ' debug' : '')

    let title: string
    let onBack: (() => void) | undefined = undefined
    let next: any | undefined = undefined;
    switch (taxonomyEditorStore.page) {
        case 'editor':
            title = viewer ? 'Taxonomy Viewer' : CARD_CONFIG['taxonomy-builder'].title
            break
        case 'approval':
            title = 'Taxonomy Approval'
            onBack = () => {
                taxonomyEditorStore.approval.saveNotesToTaxonomy()?.then(() => {
                    taxonomyEditorStore.setPage('editor')
                })
            };
            next = {
                type: 'component',
                node: <Button
                    color="primary"
                    variant="outlined"
                    endIcon={<ChevronRight/>}
                    onClick={() => {
                        taxonomyEditorStore.approval.setConfirmModalOpen(true)
                        taxonomyEditorStore.approval.saveNotesToTaxonomy()
                    }}
                    disabled={taxonomyEditorStore.approval.isBusy}>
                    Request new baseline
                </Button>
            }
            break
        case 'approval-feedback':
            title = 'Taxonomy Approval Feedback'
            onBack = () => {
                taxonomyEditorStore.setPage('editor')
            };
            break
    }

    return <MainPageFacade
        title={title}
        onBack={onBack}
        className={className}
        next={next}
        actionBarStart={
            taxonomyEditorStore.page === 'editor'
                ? <TaxonomyEditorBreadcrumbs/>
                : undefined
        }
        actionBarEnd={
            taxonomyEditorStore.page === 'editor'
                ? <TaxonomyEditorButtonBar/>
                : <Button
                    sx={{mr: 2}}
                    color="primary"
                    variant="outlined"
                    startIcon={<ChevronLeft/>}
                    onClick={onBack}>
                    Back
                </Button>
        }
    >
        {taxonomyEditorStore.page === 'editor' && <TaxonomyEditorComponent viewer={viewer}/>}
        {taxonomyEditorStore.page === 'approval' && <TaxonomyEditorApprovalComponent/>}
        {taxonomyEditorStore.page === 'approval-feedback' && <TaxonomyApprovalComponent/>}
    </MainPageFacade>
});
